import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { Send, Sparkles, RefreshCw, CalendarCheck, ImageIcon, Eye, MessageCircle } from "lucide-react";

const TEMPLATES = [
  { id: "new_addition",       label: "New addition",       Icon: Sparkles,       description: "Something new was added to their dashboard." },
  { id: "dash_updated",       label: "Dash updated",       Icon: RefreshCw,      description: "Dashboard has fresh updates." },
  { id: "calendar_completed", label: "Calendar completed", Icon: CalendarCheck,  description: "Content calendar is ready." },
  { id: "designs_uploaded",   label: "Designs uploaded",   Icon: ImageIcon,      description: "Fresh designs uploaded." },
  { id: "pending_review",     label: "Pending review",     Icon: Eye,            description: "Waiting on their review." },
  { id: "custom",             label: "Custom",             Icon: MessageCircle,  description: "Write your own subject + message." },
];

export default function SendUpdateEmailDialog({ open, onOpenChange, client, currentPage }) {
  const [template, setTemplate] = useState("dash_updated");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [resourceUrl, setResourceUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const isCustom = template === "custom";
  const tpl = TEMPLATES.find((t) => t.id === template);

  // Auto-suggest resource URL based on current page context
  React.useEffect(() => {
    if (!open || !client) return;
    const path = currentPage
      ? `/clients/${client.slug || client.id}?page=${currentPage.id}`
      : `/clients/${client.slug || client.id}`;
    setResourceUrl(path);
    setSubject("");
    setMessage("");
  }, [open, client, currentPage]);

  const send = async () => {
    if (isCustom && !subject.trim()) {
      toast.error("Subject is required for custom emails");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/clients/${client.id}/send-update`, {
        template,
        subject: subject.trim() || null,
        message: message.trim() || null,
        resource_url: resourceUrl.trim() || null,
      });
      toast.success(`Sent to ${r.data.recipients.length} recipient${r.data.recipients.length === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0a0a0a] border-zinc-800" data-testid="send-update-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-zinc-100">Send update email · {client?.name}</DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500">Reply-To is you, so clients can reply directly.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2 block">Template</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TEMPLATES.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setTemplate(id)}
                  data-testid={`email-template-${id}`}
                  className={`flex flex-col items-start gap-1 p-2.5 rounded-md border text-left transition-all ${
                    template === id ? "bg-zinc-900 border-zinc-600 text-zinc-100" : "bg-[#0a0a0a] border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-mono">{label}</span>
                </button>
              ))}
            </div>
            {tpl?.description && (
              <p className="text-[10px] text-zinc-600 mt-2">{tpl.description}</p>
            )}
          </div>

          {isCustom && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Something you should see"
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
                data-testid="email-subject" />
            </div>
          )}
          {!isCustom && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Subject (optional override)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={`[${client?.name || "Client"}] auto-generated subject`}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
                data-testid="email-subject" />
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={isCustom ? "Write your message here…" : "Add a personal note (appears above the CTA link)…"}
              rows={4}
              className="bg-[#0a0a0a] border-zinc-800 text-[12px] mt-1"
              data-testid="email-message" />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Resource link</Label>
            <Input value={resourceUrl} onChange={(e) => setResourceUrl(e.target.value)}
              placeholder="/clients/foo?page=xyz"
              className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
              data-testid="email-resource-url" />
            <p className="text-[10px] text-zinc-600 mt-1">
              Path or full URL. Leading slash paths become full dashboard URLs automatically.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400 h-8">Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-white text-black hover:bg-zinc-200 h-8" data-testid="email-send-btn">
            <Send className="w-3 h-3 mr-1.5" />{busy ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
